import type { FC } from 'react'
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline'
import { RhymeColorLegend } from './RhymeColorLegend'

interface SidebarHeaderProps {
  wordCount: number
  onClear: () => void
  onSettings?: () => void
  title?: string
}

export const SidebarHeader: FC<SidebarHeaderProps> = ({
  wordCount,
  onClear,
  onSettings,
  title = 'Word Collection'
}) => {
  const handleClear = () => {
    if (typeof window === 'undefined') {
      onClear()
      return
    }

    if (window.confirm('Clear all words from the collection? This cannot be undone.')) {
      onClear()
    }
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
      <div>
        <h2 className="text-xs font-semibold tracking-wide uppercase text-gray-900">{title}</h2>
        <p
          data-testid="word-collection-word-count"
          className="text-[0.65rem] text-gray-500"
        >
          {wordCount} word{wordCount === 1 ? '' : 's'}
        </p>
        <div className="mt-2">
          <RhymeColorLegend />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleClear}
          className="group flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[0.6rem] font-medium uppercase tracking-widest text-gray-600 transition hover:border-red-300 hover:text-red-600"
          data-testid="word-collection-clear"
        >
          <TrashIcon className="h-3 w-3" aria-hidden />
          <span className="hidden sm:inline">Clear</span>
        </button>
        <button
          type="button"
          onClick={onSettings}
          disabled={!onSettings}
          className="group flex h-8 w-8 items-center justify-center rounded-full border border-transparent bg-gray-100 text-gray-500 transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Word collection settings"
          data-testid="word-collection-settings"
        >
          <Cog6ToothIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}

export default SidebarHeader
